import React from 'react';
import PropTypes from 'prop-types';
import SettingsSidebar from 'pages/preferences/settings-sidebar';

// The settings screens (Billing, Taxes, Payment methods, Pricebook, Forms and
// the Preferences pages): the settings menu on the left, then the page, a
// title with one line on what it's for, and its actions on the right.
// Styles: styles/settings-page.scss (.sp-*).
//
//   <SettingsPage title="Taxes" description="Rates you can add to estimates and invoices."
//     actions={<Button className="sp-button sp-button--primary" icon="add" text="Add tax" />}>
//     <SettingsCard flush>{list}</SettingsCard>
//   </SettingsPage>
//
// `sidebar` swaps the settings menu for another section's, e.g. the account
// menu (components/AccountSidebar) on Change password.
const SettingsPage = ({ title, description, actions, tabs, sidebar = <SettingsSidebar />, children }) => {
  const page = (
    <main className="sp" aria-labelledby="sp-title">
      <header className="sp-head">
        <div className="sp-head__text">
          <h1 className="sp-head__title" id="sp-title">{title}</h1>
          {description && <p className="sp-head__description">{description}</p>}
        </div>
        {actions && <div className="sp-head__actions">{actions}</div>}
      </header>
      {tabs}
      {children}
    </main>
  );
  return (
    <div className="row no-gutters">
      <div className="col-lg-2 sidebar-responsive">
        {sidebar}
      </div>
      <div className="col-lg-10">{page}</div>
    </div>
  );
};

SettingsPage.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  actions: PropTypes.node,
  tabs: PropTypes.node,
  sidebar: PropTypes.node,
  children: PropTypes.node,
};

// A white card on a settings page. With form fields inside it gets the side
// panels' field styles (form-drawer.scss, `.fd`), so settings forms use
// FormSection, Toggle, Choice and FieldError like the New/Edit panels: in a
// wide card each section's title sits in a column on the left.
// `flush` drops the padding, for a list (DataList) that runs edge to edge.
// `footer` is pinned under the fields, e.g. the Save button.
export const SettingsCard = ({ title, description, aside, flush, footer, className = '', children }) => (
  <section className={`sp-card fd${flush ? ' sp-card--flush' : ''} ${className}`}>
    {(title || aside) && (
      <header className="sp-card__head">
        <div>
          {title && <h2 className="sp-card__title">{title}</h2>}
          {description && <p className="sp-card__description">{description}</p>}
        </div>
        {aside && <div className="sp-card__aside">{aside}</div>}
      </header>
    )}
    <div className="sp-card__body">{children}</div>
    {footer && <footer className="sp-card__footer">{footer}</footer>}
  </section>
);

SettingsCard.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  aside: PropTypes.node,
  flush: PropTypes.bool,
  footer: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

// Section tabs at the top of a settings page (e.g. Billing: Overview, Billing
// history, Card). tabs: [{ id, label }]; `onChange(id)`.
export const SettingsTabs = ({ tabs, selected, onChange, label }) => (
  <div className="sp-tabs" role="tablist" aria-label={label}>
    {tabs.map((tab) => (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={tab.id === selected}
        className={`sp-tab${tab.id === selected ? ' is-selected' : ''}`}
        onClick={() => onChange(tab.id)}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

SettingsTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.node })).isRequired,
  selected: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default SettingsPage;
